import type { PaymentFormValues } from 'Src/types/payment';
import { BucketFormikInput, BucketFormikInputProps, asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, Fragment, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { PersonInfoFormContent as PersonField } from 'Components/widgets/contact-info/contact-info-form/PersonInfoFormContent';
import { t } from 'Src/i18n';
import { useCountryList } from 'Src/hooks/use-country-list/useCountryList';
import { getValidationSchema } from 'Views/booking/BookingEngine.query';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { getSchemaValidators } from 'Components/widgets/contact-info/ContactInfoUtils';
import { Title } from 'Components/frameworks/title/Title';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { classnames } from 'Utils/classnames/classnames.utils';
import {
  CUSTOMER_ACCOUNT_GET_PROFILE_DATA,
  PROPOSAL_GET_SCHEMA,
} from 'Utils/request/requestDictionary';
import { ADDRESS_FIELDS_CLASSES } from 'Components/widgets/contact-info/constants';
import { getProfileQuery } from 'Views/account/profile/ProfileView.queries';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { getFromSessionStorage } from 'Client/session-storage-api/sessionStorageApi';
import { decompressFromBase64 } from 'lz-string';
const billingPersonField = [
  {
    display: true,
    type: 'string',
    name: 'last_name',
    required: true,
    validators: [],
  },
  {
    display: true,
    type: 'string',
    name: 'first_name',
    required: true,
    validators: [],
  },
];
type BillingAddressFormProps = {
  i18n: I18N;
  path: string;
};
export const BillingAddressForm = ({ i18n, path }: BillingAddressFormProps) => {
  const formik = useFormikContext<PaymentFormValues>();
  const appContext = useAppContext();
  const { billingDetails } = formik.values;
  const countries = useCountryList();
  const { data: billingAddressSchema } = useGQL({
    query: getValidationSchema('', 'billing', billingDetails.countryCode),
    queryOptions: {
      requestName: PROPOSAL_GET_SCHEMA,
    },
  });
  const { data: profileData } = useGQL({
    query: getProfileQuery(),
    queryOptions: {
      requestName: CUSTOMER_ACCOUNT_GET_PROFILE_DATA,
    },
    ignore:
      (!appContext.isUserLogged && !appContext.isSellerLogged) ||
      (appContext.isCmta && !isFeatureFlipped(appContext)('booking.cmta.billingAddressPrefill')),
  });
  const profile =
    (profileData as { profile?: Record<string, unknown> })?.profile ||
    JSON.parse(decompressFromBase64(getFromSessionStorage('profile') || ''));
  const billingAddressFields =
    (
      billingAddressSchema as {
        validationSchema?: Array<{ fields?: Array<{ name: string; values?: unknown[] }> }>;
      }
    )?.validationSchema?.[0]?.fields?.filter(({ name }) => name !== 'countryCode') || [];
  const validators = getSchemaValidators(
    billingAddressFields as Parameters<typeof getSchemaValidators>[0],
    {
      withRequired: true,
      requiredMessage: () => t('common.form.errors.requiredField'),
    },
  );
  useEffect(() => {
    if (
      billingAddressFields?.length &&
      billingDetails.countryCode === profile?.address?.countryCode
    ) {
      [
        ...billingAddressFields,
        {
          name: 'countryCode',
        },
        {
          name: 'firstName',
        },
        {
          name: 'lastName',
        },
      ].forEach(({ name, values = [] }) => {
        const profileValue = profile?.[name] || profile?.address?.[name] || '';
        if (values?.length) {
          const value = (values as Array<{ code: string; label: string }>).find(({ code, label }) =>
            [code, label].includes(profileValue.toUpperCase()),
          );
          value && formik.setFieldValue(`${path}.${name}`, value.code, false);
        } else {
          formik.setFieldValue(`${path}.${name}`, profileValue, false);
        }
      });
    }
  }, [JSON.stringify(profile), billingAddressFields?.length]);
  return (
    <Fragment>
      <Title
        {...asWidgetProps<ComponentProps<typeof Title>>({
          className: 'mb-10',
          theme: 'title-brand-n2',
        })}
      >
        {t('booking.banking.billingAddressTitle')}
      </Title>

      <Capsule
        {...asWidgetProps<ComponentProps<typeof Capsule>>({
          className: 'mb-20',
          header: 'none',
        })}
      >
        <div className="px-20 py-10">
          <BucketFormikInput
            {...asWidgetProps<BucketFormikInputProps>({
              isRequired: true,
              label: t('common.participants.address.countryCode'),
              name: `${path}.countryCode`,
              options: countries,
              type: 'select',
            })}
          />
        </div>
        <PersonField
          {...asWidgetProps<ComponentProps<typeof PersonField>>({
            id: `${path}-person`,
            i18n: i18n,
            attendeeIdentifier: path,
            attendeeSchema: billingPersonField,
            addressSchema: [],
          })}
        />
        <div className="flex w-full flex-wrap px-10">
          {billingAddressFields.map((field) => {
            const { name: fieldName, values } = field;
            const required = (field as { required?: boolean }).required;
            return (
              <div
                className={classnames(
                  'w-full p-10 pb-20 pt-0',
                  ADDRESS_FIELDS_CLASSES[fieldName] || 'w-full',
                )}
                key={fieldName}
              >
                <BucketFormikInput
                  {...asWidgetProps<BucketFormikInputProps>({
                    autoComplete: 'nope',
                    fieldName: fieldName,
                    label: t(`common.participants.address.${fieldName}`),
                    name: `${path}.${fieldName}`,
                    key: fieldName,
                    type: values?.length ? 'select' : 'default',
                    placeholder:
                      values?.length &&
                      t('common.form.labels.fieldSelection', {
                        field: t(`common.participants.address.${fieldName}`),
                      }),
                    options:
                      values?.length &&
                      (values as Array<{ code: string; label: string }>).map(({ code, label }) => ({
                        name: label,
                        value: code,
                      })),
                    namePrefix: path,
                    isRequired: required,
                    validate: validators?.[fieldName],
                  })}
                />
              </div>
            );
          })}
        </div>
      </Capsule>
    </Fragment>
  );
};
